import { Link, createFileRoute } from "@tanstack/react-router";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks, HowWeTested } from "@/components/eeat-blocks";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/methodologie";
import { getAuthor } from "@/lib/authors";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { ogImageMeta } from "@/lib/og";
import { SITE } from "@/lib/site";

const PATH = "/methodologie";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/methodologie")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "Onze Testmethode: Hoe Wij Casinobetalingen Meten" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "Onze Testmethode: Hoe Wij Casinobetalingen Meten" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("methodologie"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Methodologie", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: MethodologyPage,
});

function MethodologyPage() {
  const budget = new LinkBudget(PATH);

  const takeaways = [
    "Elk cijfer op deze site komt uit een eigen test met echt geld.",
    "Uitbetaalsnelheid weegt met 30% het zwaarst in onze rangschikking.",
    "De casinolijst is op elke pagina identiek en wordt niet per pagina herschikt.",
    "Schrijver en factchecker zijn altijd verschillende personen.",
    "Advertentielinks beïnvloeden de volgorde niet en kunnen geen bevinding laten verwijderen.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <nav aria-label="Broodkruimels" className="mb-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Methodologie</span>
      </nav>

      <h1 className="max-w-4xl text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        Methodologie: zo beoordelen wij online casino&apos;s en betaalmethoden
      </h1>
      <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{guide.description}</p>

      <div className="mt-6 max-w-3xl">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>

      <div className="mt-8 max-w-3xl">
        <KeyTakeaways items={takeaways} />
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <HowWeTested
            subject="elke aanbieder in onze lijst"
            providers={10}
            period="november 2026"
            extra="Alle metingen worden vastgelegd met tijdstempels en door een tweede redacteur nagelopen voordat een pagina live gaat."
          />

          <section className="mt-12" aria-label="Onze testmethode">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="onze testmethode"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over onze werkwijze
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
            />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
